import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldCheck, UserRound, Users } from 'lucide-react';
import type { PlatformUserDto } from '@his/shared';
import { get, post, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, Input } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { useToast } from '@/components/ui/toast';
import { isValidTotp, TotpField } from './OwnerConfirm';
import { ROLE_NOTES } from './PlatformStaffPage';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const EMPTY = {
  name: '',
  fatherName: '',
  cnic: '',
  email: '',
  phone: '',
  role: 'contact_person',
  password: '',
};

type PersonForm = typeof EMPTY;

function formFor(user: PlatformUserDto | null): PersonForm {
  if (!user) return EMPTY;
  return {
    ...EMPTY,
    name: user.name,
    fatherName: user.fatherName ?? '',
    cnic: user.cnic ?? '',
    email: user.email,
    phone: user.phone ?? '',
    role: user.role,
  };
}

export function PlatformStaffFormPage() {
  const { staffId = '' } = useParams();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/owner/staff');

  const staff = useQuery({
    queryKey: ['platform-staff', ''],
    queryFn: () => get<PlatformUserDto[]>('/platform/staff'),
    enabled: Boolean(staffId),
  });
  const user = staff.data?.find((row) => row.id === staffId) ?? null;

  if (staffId && !user) {
    return (
      <FormScreen title="Edit account" icon={Users} backTo={returnTo}>
        <RecordState error={staff.error} missing={Boolean(staff.data)} />
      </FormScreen>
    );
  }

  return <PlatformStaffForm key={staffId || 'new'} user={user} returnTo={returnTo} />;
}

function PlatformStaffForm({ user, returnTo }: { user: PlatformUserDto | null; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [initial] = useState(() => formFor(user));
  const [form, setForm] = useState(initial);
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [reason, setReason] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const payload = { totpCode, reason: reason.trim() };
      return user
        ? put(`/platform/staff/${user.id}`, {
            ...payload,
            name: form.name,
            fatherName: form.fatherName || null,
            cnic: form.cnic || null,
            phone: form.phone || null,
            role: form.role,
            isActive,
          })
        : post('/platform/staff', {
            ...payload,
            name: form.name,
            fatherName: form.fatherName || undefined,
            cnic: form.cnic || undefined,
            email: form.email,
            phone: form.phone || undefined,
            role: form.role,
            password: form.password,
          });
    },
    onSuccess: () => {
      toast.success(user ? 'Account updated' : 'Account created — give them the temporary password directly; it is not emailed.');
      void queryClient.invalidateQueries({ queryKey: ['platform-staff'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const valid = user ? form.name.length >= 2 : form.name.length >= 2 && form.email.includes('@') && form.password.length >= 12;
  const ready = valid && reason.trim().length >= 5 && isValidTotp(totpCode);
  const dirty = !save.isSuccess && (JSON.stringify(form) !== JSON.stringify(initial) || isActive !== (user?.isActive ?? true) || reason !== '' || totpCode !== '');
  const sessionsEnd = user ? form.role !== user.role || !isActive : false;

  return (
    <FormScreen
      title={user ? `Edit ${user.name}` : 'Add a platform account'}
      description={
        user
          ? user.assignedTenants
            ? `Contact for ${user.assignedTenants} hospital(s). Reassign those before changing the role or deactivating.`
            : 'The email is the sign-in and cannot be changed here.'
          : 'Contact persons sign in to the client portal; everyone else to the owner console.'
      }
      icon={Users}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title="Person" icon={UserRound}>
        <Field label="Full name" required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Father's name">
          <Input value={form.fatherName} onChange={(e) => setForm({ ...form, fatherName: e.target.value })} />
        </Field>
        <Field label="CNIC" hint="13 digits, with or without dashes">
          <Input className="tabular" value={form.cnic} onChange={(e) => setForm({ ...form, cnic: e.target.value })} />
        </Field>
        <Field label="Phone">
          <PhoneText value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title="Sign-in" icon={KeyRound}>
        <Field label="Email" required hint="This is their sign-in" className="sm:col-span-2">
          <Input type="email" value={form.email} readOnly={Boolean(user)} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label="Role" required hint={ROLE_NOTES[form.role]}>
          <ChoiceButtons
            layout="chips"
            value={form.role}
            onChange={(role) => setForm({ ...form, role })}
            options={Object.keys(ROLE_NOTES).map((role) => ({ value: role, label: humanize(role, 'platform_role') }))}
          />
        </Field>
        {user ? (
          <Checkbox className="sm:pt-6" checked={isActive} onCheckedChange={setIsActive} label="Account is active" />
        ) : (
          <Field label="Password" required hint="At least 12 characters">
            <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
        )}
      </FormSection>

      <FormSection title="Confirm" icon={ShieldCheck} columns={1}>
        {user ? (
          sessionsEnd && <Alert>Their current sessions end as soon as you save.</Alert>
        ) : (
          <Alert>They must set their own password and pair an authenticator at their first sign-in.</Alert>
        )}
        <Field label="Reason" required hint="Recorded in the platform audit trail — a ticket number is ideal.">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <TotpField value={totpCode} onChange={setTotpCode} />
        {error && <Alert variant="destructive">{error}</Alert>}
      </FormSection>

      <FormActions submitLabel={user ? 'Save' : 'Create'} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} bleed={false} />
    </FormScreen>
  );
}
